// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file TextInput.tsx
 * @input Uses React, useId, ChangeEvent, Field, Icon, InputGroupContext
 * @output Exports TextInput component, TextInputProps
 * @position Core implementation; consumed by index.ts, tested by TextInput.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/TextInput/TextInput.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/TextInput/TextInput.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/TextInput/index.ts (exports if types change)
 * - /apps/storybook/stories/TextInput.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/TextInput/ (showcase blocks)
 */
import { useId, useOptimistic, useTransition, useCallback, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, radiusVars, typographyVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { Field, inputWrapperStyles, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputStatusFocusWithinStyles } from "../Field/index.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { Spinner } from "../Spinner/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { getInputARIA } from "../utils/index.js";
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};
import { groupStyles } from "../InputGroup/groupStyles.js";

// Re-export shared types for convenience

import { mergeProps, mergeRefs } from "../utils/index.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { useInputContainer } from "../hooks/useInputContainer.js";
import { useInputGroup } from "../InputGroup/InputGroupContext.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * A text input component for collecting user input.
 *
 * @example
 * ```
 * <TextInput label="Name" value={name} onChange={setName} />
 * <TextInput label="Search" isLabelHidden value={query} onChange={setQuery} />
 * ```
 */
export function TextInput({
  type = 'text',
  label,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  disabledMessage,
  startIcon,
  status,
  size: sizeProp,
  onChange,
  changeAction,
  isLoading = false,
  value,
  placeholder,
  labelTooltip,
  hasClear = false,
  hasAutoFocus = false,
  htmlName,
  onEnter,
  onKeyDown,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const size = useSize(sizeProp, 'md');
  const id = useId();
  const inputLabelID = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const inputGroup = useInputGroup();
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the input container (which already exists) and
  // the input stays perceivable via aria-disabled instead of the native
  // disabled attribute. The field is made read-only so it can't be typed into,
  // and value mutation is blocked by the isDisabled guard in handleChange.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the input, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const statusIconMap = {
    warning: 'warning',
    error: 'error',
    success: 'success'
  };
  const statusIconColorMap = {
    warning: 'warning',
    error: 'error',
    success: 'success'
  };
  const {
    ariaLabelledBy,
    ariaDescribedBy
  } = getInputARIA(inputLabelID, [description ? descriptionID : null, status?.message ? statusMessageID : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null], inputGroup);
  const handleChange = e => {
    // Value can't change while showing a disabled message (the field is
    // read-only and non-native-disabled), but guard the handler too so the
    // optimistic value and callbacks never fire.
    if (isDisabled) {
      return;
    }
    const newValue = e.target.value;
    onChange?.(newValue, e);
    if (changeAction && !e.defaultPrevented) {
      startTransition(async () => {
        setOptimisticValue(newValue);
        await changeAction(newValue, e);
      });
    }
  };

  // Handle clear button click
  const handleClear = useCallback(() => {
    onChange?.('', null);
    inputRef.current?.focus();
  }, [onChange]);

  // Focus input when clicking anywhere on the wrapper (icons, padding, etc.)
  const {
    onClick: handleWrapperClick,
    onMouseUp: handleWrapperMouseUp
  } = useInputContainer({
    containerRef,
    inputRef,
    disabled: isDisabled
  });
  const inputWrapper = /*#__PURE__*/_jsxs("div", {
    ref: el => {
      containerRef.current = el;
      // Anchor + hover/focus listeners for the disabled-message tooltip.
      // Handlers are gated internally by isEnabled, and anchor names
      // compose, so attaching unconditionally is safe.
      disabledMessageTooltip.ref(el);
    },
    onClick: handleWrapperClick,
    onMouseUp: handleWrapperMouseUp,
    ...mergeProps(themeProps('text-input', {
      size,
      status: status?.type ?? null
    }), stylex.props(inputWrapperStyles.base, sizeStyles[size], isDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type], inputGroup && groupStyles.inGroup, xstyle), className, style),
    children: [startIcon && renderIconSlot(startIcon, {
      size: 'sm',
      color: 'secondary'
    }), inputGroup && /*#__PURE__*/_jsx(VisuallyHidden, {
      id: inputLabelID,
      children: label
    }), /*#__PURE__*/_jsx("input", {
      ...rest,
      ref: mergeRefs(ref, inputRef),
      id: id,
      name: htmlName,
      type: type,
      value: optimisticValue,
      onChange: handleChange,
      onKeyDown: onEnter || onKeyDown ? e => {
        if (e.key === 'Enter') {
          onEnter?.();
        }
        onKeyDown?.(e);
      } : undefined,
      placeholder: placeholder
      // With a disabledMessage the input keeps focusability via aria-disabled
      // so the reason is focus-discoverable; readOnly + the handleChange guard
      // keep the value from changing.
      ,
      disabled: isDisabled && !showsDisabledMessage,
      "aria-disabled": showsDisabledMessage ? 'true' : undefined,
      readOnly: showsDisabledMessage || undefined,
      autoFocus: hasAutoFocus,
      "data-autofocus": hasAutoFocus || undefined,
      "aria-describedby": ariaDescribedBy,
      "aria-required": isRequired === true ? 'true' : undefined,
      "aria-invalid": status?.type === 'error' ? 'true' : undefined,
      "aria-busy": isBusy || undefined,
      "aria-labelledby": ariaLabelledBy,
      ...{
        0: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5"
        },
        1: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc"
        }
      }[!!isDisabled << 0]
    }), hasClear && value !== '' && !isDisabled && /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: handleClear,
      "aria-label": t('@astryx.textInput.clearLabel', {
        label
      }),
      ...{
        className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
      },
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "close",
        size: "sm",
        color: "secondary"
      })
    }), isBusy && /*#__PURE__*/_jsx(Spinner, {
      size: "sm"
    }), status && !inputGroup && /*#__PURE__*/_jsx(Icon, {
      icon: statusIconMap[status.type],
      size: "md",
      color: statusIconColorMap[status.type]
    })]
  });
  if (inputGroup) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [inputWrapper, showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
    });
  }
  return /*#__PURE__*/_jsxs(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: id,
    descriptionID: description ? descriptionID : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    labelTooltip: labelTooltip,
    width: width,
    children: [inputWrapper, showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
TextInput.displayName = 'TextInput';